import React, { useEffect, useState, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useSocketEvent } from '../context/SocketContext';
import { StatusBadge, PaymentBadge } from '../components/Badges';
import PatientDetailModal from '../components/PatientDetailModal';
import ConfirmDialog from '../components/ConfirmDialog';
import { Search, Download, Trash2, Eye, Users, Archive, Inbox, Layers, ArrowUpDown } from 'lucide-react';
import ViewModeSwitcher, { useViewMode } from '../components/ViewModeSwitcher';
import WhatsAppButton from '../components/WhatsAppButton';

// View modes for the patient list:
//  - 'active'   : only patients not archived (today's & past active records)
//  - 'archived' : only archived patients (manually removed, or cleared by a daily reset)
//  - 'all'      : everything, active and archived together
// This toggle exists specifically so that after using "Reset Daily Tokens"
// (which archives, but never deletes, patient records), the doctor and
// receptionist can still find and view every patient that was ever
// registered - nothing is ever actually gone from the database.
const VIEW_MODES = [
  { key: 'active', label: 'Active', icon: Inbox },
  { key: 'archived', label: 'Archived / Past', icon: Archive },
  { key: 'all', label: 'All Records', icon: Layers },
];

const SORT_OPTIONS = [
  { key: 'createdAt-desc', label: 'Newest first' },
  { key: 'createdAt-asc', label: 'Oldest first' },
  { key: 'name-asc', label: 'Name (A–Z)' },
  { key: 'name-desc', label: 'Name (Z–A)' },
  { key: 'status-asc', label: 'Status / Type' },
  { key: 'paymentStatus-asc', label: 'Payment: Unpaid first' },
  { key: 'paymentStatus-desc', label: 'Payment: Paid first' },
  { key: 'tokenNumber-asc', label: 'Token number (low–high)' },
];

function sortPatients(list, sortKey) {
  const [field, dir] = sortKey.split('-');
  const mult = dir === 'asc' ? 1 : -1;
  const sorted = [...list].sort((a, b) => {
    let av, bv;
    switch (field) {
      case 'name':
        av = a.name.toLowerCase();
        bv = b.name.toLowerCase();
        break;
      case 'status':
        av = a.status;
        bv = b.status;
        break;
      case 'paymentStatus':
        // Custom order so "unpaid first" / "paid first" reads naturally,
        // not alphabetically (alphabetical would put "partial" oddly in
        // the middle regardless of what the user actually wants to see).
        // eslint-disable-next-line no-case-declarations
        const order = dir === 'asc' ? { unpaid: 0, partial: 1, paid: 2 } : { paid: 0, partial: 1, unpaid: 2 };
        av = order[a.billing?.paymentStatus || 'unpaid'];
        bv = order[b.billing?.paymentStatus || 'unpaid'];
        return av - bv; // direction already baked into the order map above
      case 'tokenNumber':
        av = a.tokenNumber || 0;
        bv = b.tokenNumber || 0;
        break;
      case 'createdAt':
      default:
        av = new Date(a.createdAt).getTime();
        bv = new Date(b.createdAt).getTime();
        break;
    }
    if (av < bv) return -1 * mult;
    if (av > bv) return 1 * mult;
    return 0;
  });
  return sorted;
}

export default function AllPatientsPage() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('all');
  const [sortKey, setSortKey] = useState('createdAt-desc');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Group all visits by patientUID — shows one patient block with all their visits
  const [groupBy, setGroupBy] = useState('none'); // 'none'|'patient'|'date'
  const [listMode, setListMode] = useViewMode('allpatients_view', 'list');
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const { showToast } = useToast();
  const { user } = useAuth();

  const loadPatients = useCallback(async (searchTerm = '') => {
    setLoading(true);
    try {
      // includeArchived=true always fetches everything; we then filter
      // client-side by viewMode so switching tabs feels instant and we
      // don't need a round trip for every toggle click.
      const res = await api.get('/patients', {
        params: { search: searchTerm, limit: 500, includeArchived: 'true' },
      });
      setPatients(res.data.patients);
    } catch (err) {
      showToast('Could not load patients.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  useEffect(() => {
    const debounce = setTimeout(() => loadPatients(search), 350);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  function handleUpdated(updated) {
    setPatients((prev) => {
      const exists = prev.some((p) => p._id === updated._id);
      if (exists) return prev.map((p) => (p._id === updated._id ? updated : p));
      return [updated, ...prev];
    });
  }

  // Real-time: whenever any patient is created, edited, archived, or the
  // daily queue is reset, refresh this list immediately so the doctor and
  // receptionist always see accurate, current data without refreshing the
  // page or waiting on a timer.
  useSocketEvent('queue:updated', () => loadPatients(search));
  useSocketEvent('queue:reset', () => loadPatients(search));
  useSocketEvent('patient:updated', (payload) => {
    if (payload?.patient) handleUpdated(payload.patient);
  });
  useSocketEvent('patient:created', (payload) => {
    if (payload?.patient) handleUpdated(payload.patient);
  });
  useSocketEvent('patient:archived', (payload) => {
    if (payload?.patient) handleUpdated(payload.patient);
  });

  async function handleDownloadCSV() {
    setExporting(true);
    try {
      const token = sessionStorage.getItem('dq_token');
      const apiBase = process.env.REACT_APP_API_URL || '/api';
      const res = await fetch(`${apiBase}/patients/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `patients_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showToast('Patient list downloaded as CSV (includes active and archived records).', 'success');
    } catch (err) {
      showToast('Could not download CSV file.', 'error');
    } finally {
      setExporting(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await api.delete(`/patients/${deleteTarget._id}`);
      showToast('Patient removed from active list. Record is preserved in the database — switch to "Archived / Past" to find it again.', 'success');
      handleUpdated(res.data.patient);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not delete patient.', 'error');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }

  const filteredByMode = sortPatients(
    patients.filter((p) => {
      if (viewMode === 'active') return !p.isArchived;
      if (viewMode === 'archived') return p.isArchived;
      return true; // 'all'
    }),
    sortKey
  );

  const activeCount   = patients.filter((p) => !p.isArchived).length;
  const archivedCount = patients.filter((p) => p.isArchived).length;

  // Group by patientUID — every visit by the same patient is collected under one header
  const patientGroups = React.useMemo(() => {
    const map = {};
    filteredByMode.forEach(p => {
      const uid = p.patientUID;
      if (!map[uid]) map[uid] = { uid, name: p.name, phone: p.phone, age: p.age, gender: p.gender, visits: [] };
      map[uid].visits.push(p);
    });
    // Sort visits within each group newest-first
    Object.values(map).forEach(g => g.visits.sort((a,b) => (b.tokenDate||'') > (a.tokenDate||'') ? 1 : -1));
    return Object.values(map).sort((a,b) => (b.visits[0]?.tokenDate||'') > (a.visits[0]?.tokenDate||'') ? 1 : -1);
  }, [filteredByMode]);

  return (
    <AppLayout title="All Patients" subtitle="Search, view, edit, and manage every patient record — past and present">
      <div
        style={{
          display: 'flex',
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          padding: 4,
          gap: 4,
          marginBottom: 16,
          width: 'fit-content',
        }}
      >
        {VIEW_MODES.map(({ key, label, icon: Icon }) => {
          const count = key === 'active' ? activeCount : key === 'archived' ? archivedCount : patients.length;
          const active = viewMode === key;
          return (
            <button
              key={key}
              onClick={() => setViewMode(key)}
              className={active ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
              style={{ gap: 6 }}
            >
              <Icon size={14} />
              {label}
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  background: active ? 'rgba(255,255,255,0.25)' : 'var(--color-primary-soft)',
                  color: active ? 'white' : 'var(--color-primary)',
                  borderRadius: 100,
                  padding: '1px 7px',
                  minWidth: 18,
                  textAlign: 'center',
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {viewMode === 'archived' && archivedCount > 0 && (
        <div
          className="card"
          style={{
            padding: '10px 16px',
            marginBottom: 16,
            fontSize: 13,
            color: 'var(--color-ink-soft)',
            background: 'var(--color-info-soft)',
            border: 'none',
          }}
        >
          These patients were removed from the active queue (manually, or via "Reset Daily Tokens"), but their full record, history, and attachments are still safely stored here.
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 260px' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-ink-faint)' }} />
          <input
            className="input"
            style={{ paddingLeft: 36 }}
            placeholder="Search by name, ID, or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div style={{ position: 'relative', minWidth: 190 }}>
          <ArrowUpDown size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-ink-faint)', pointerEvents: 'none' }} />
          <select
            className="select"
            style={{ paddingLeft: 34, fontWeight: 600 }}
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            aria-label="Sort patients by"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.key} value={opt.key}>{opt.label}</option>
            ))}
          </select>
        </div>
        <ViewModeSwitcher mode={listMode} onChange={setListMode} />
        <button className="btn btn-primary" onClick={handleDownloadCSV} disabled={exporting}>
          <Download size={15} />
          {exporting ? 'Preparing...' : 'Download CSV'}
        </button>
        {/* Group by dropdown */}
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <Layers size={14} color="var(--color-ink-faint)" />
          <select className="select" style={{ minWidth:170, fontWeight:600 }} value={groupBy} onChange={e => setGroupBy(e.target.value)}>
            <option value="none">No grouping</option>
            <option value="patient">Group by Patient</option>
            <option value="date">Group by Date</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'grid', gap: 10 }}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="skeleton" style={{ height: 56, borderRadius: 8 }} />
          ))}
        </div>
      ) : filteredByMode.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--color-ink-faint)' }}>
          <Users size={36} style={{ marginBottom: 12, opacity: 0.5 }} />
          <p style={{ margin: 0, fontWeight: 600 }}>
            {viewMode === 'archived' ? 'No archived patients yet.' : 'No patients found.'}
          </p>
        </div>
      ) : groupBy === 'patient' ? (
        /* ── Grouped by patient view ──────────────────────────────────────── */
        <div style={{ display: 'grid', gap: 12 }}>
          {patientGroups.map(g => {
            const isExpanded = expandedGroups.has(g.uid);
            const toggleGroup = () => setExpandedGroups(prev => {
              const next = new Set(prev);
              isExpanded ? next.delete(g.uid) : next.add(g.uid);
              return next;
            });
            const totalBilled   = g.visits.reduce((s,v) => s + (v.billing?.amount||0), 0);
            const totalPaid     = g.visits.reduce((s,v) => s + (v.billing?.amountPaid||0), 0);
            const totalPending  = g.visits.reduce((s,v) => s + Math.max((v.billing?.amount||0)-(v.billing?.amountPaid||0),0), 0);
            return (
              <div key={g.uid} className="card" style={{ overflow: 'hidden' }}>
                {/* Patient group header */}
                <div
                  role="button" onClick={toggleGroup}
                  style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', cursor:'pointer', background:'var(--color-surface)', borderBottom: isExpanded ? '1px solid var(--color-border)' : 'none' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:36, height:36, borderRadius:'50%', background:'var(--color-primary-soft)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:14, color:'var(--color-primary)', flexShrink:0 }}>
                      {g.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight:700, fontSize:14 }}>{g.name}</div>
                      <div style={{ fontSize:12, color:'var(--color-ink-faint)', display:'flex', gap:10 }}>
                        <span style={{ fontFamily:'var(--font-mono)', color:'var(--color-primary)' }}>{g.uid}</span>
                        {g.phone && <span>📱 {g.phone}</span>}
                        {g.age && <span>{g.age}y {g.gender}</span>}
                      </div>
                    </div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:16 }}>
                    <div style={{ textAlign:'right', fontSize:12 }}>
                      <div style={{ color:'var(--color-ink-faint)' }}>{g.visits.length} visit{g.visits.length!==1?'s':''}</div>
                      <div style={{ fontWeight:700 }}>Total ₹{totalBilled.toLocaleString('en-IN')}</div>
                    </div>
                    <div style={{ textAlign:'right', fontSize:12 }}>
                      <div style={{ color:'var(--color-success)', fontWeight:600 }}>Paid ₹{totalPaid.toLocaleString('en-IN')}</div>
                      {totalPending > 0 && <div style={{ color:'var(--color-warning)', fontWeight:700 }}>Due ₹{totalPending.toLocaleString('en-IN')}</div>}
                    </div>
                    <span style={{ color:'var(--color-ink-faint)', fontSize:12 }}>{isExpanded ? '▲' : '▼'}</span>
                  </div>
                </div>
                {/* Visit rows within this patient group */}
                {isExpanded && (
                  <div>
                    {g.visits.map((p, vi) => (
                      <div key={p._id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 16px', borderBottom:'1px solid var(--color-border)', background: vi%2===0 ? 'transparent' : 'var(--color-surface)', gap:10, flexWrap:'wrap' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <span style={{ fontSize:11, color:'var(--color-ink-faint)', minWidth:60 }}>Visit {vi+1}</span>
                          <div>
                            <div style={{ fontSize:12, fontWeight:600 }}>{p.tokenDate} — Token #{p.tokenNumber}</div>
                            <div style={{ fontSize:11, color:'var(--color-ink-faint)', display:'flex', gap:8 }}>
                              {p.appointmentSlot && <span>🕐 {p.appointmentSlot}</span>}
                              {p.reasonForVisit && <span style={{ fontStyle:'italic' }}>{p.reasonForVisit}</span>}
                            </div>
                          </div>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <StatusBadge status={p.status} />
                          <PaymentBadge status={p.billing?.paymentStatus||'unpaid'} />
                          <span style={{ fontSize:12, fontWeight:600 }}>₹{(p.billing?.amount||0).toLocaleString('en-IN')}</span>
                          <button className="btn btn-outline btn-sm" onClick={() => setSelectedPatient(p)}>
                            <Eye size={12} /> View
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : groupBy === 'date' ? (
        /* ── Group by Date ─────────────────────────────────────────────────── */
        (() => {
          const dateMap = {};
          filteredByMode.forEach(p => {
            const d = p.tokenDate || 'No date';
            if (!dateMap[d]) dateMap[d] = [];
            dateMap[d].push(p);
          });
          return (
            <div style={{ display:'grid', gap:16 }}>
              {Object.entries(dateMap).sort(([a],[b]) => b>a?1:-1).map(([date, dps]) => (
                <div key={date}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8, padding:'6px 12px', background:'var(--color-surface)', borderRadius:8, border:'1px solid var(--color-border)' }}>
                    <Layers size={13} color="var(--color-ink-faint)" />
                    <span style={{ fontWeight:700, fontSize:13 }}>{date}</span>
                    <span style={{ fontSize:12, color:'var(--color-ink-faint)' }}>· {dps.length} patient{dps.length!==1?'s':''}</span>
                  </div>
                  <div style={{ border:'1px solid var(--color-border)', borderRadius:8, overflow:'hidden' }}>
                    {dps.map((p, vi) => (
                      <div key={p._id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 16px', borderTop: vi===0?'none':'1px solid var(--color-border)', background: vi%2===0?'transparent':'var(--color-surface)', gap:10, flexWrap:'wrap' }}>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontWeight:700, fontSize:13 }}>{p.name}</div>
                          <div style={{ fontSize:12, color:'var(--color-ink-faint)' }}>{p.patientUID} · Token #{p.tokenNumber}{p.gender?` · ${p.gender}`:''}{p.age?`, ${p.age}y`:''}{p.phone?` · ${p.phone}`:''}</div>
                        </div>
                        <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'nowrap' }}>
                          <StatusBadge status={p.status} />
                          <PaymentBadge status={p.billing?.paymentStatus||'unpaid'} />
                          {p.phone && <WhatsAppButton patient={p} slot={p.appointmentSlot} date={p.tokenDate} tokenNumber={p.tokenNumber} size="xs" />}
                          <button className="btn btn-icon btn-ghost" title="View / Edit" onClick={() => setSelectedPatient(p)}><Eye size={15}/></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })()
      ) : listMode === 'grid' ? (
        /* ── Grid view ── */
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:10 }}>
          {filteredByMode.map(p => (
            <div key={p._id} className="card" style={{ padding:14, display:'flex', flexDirection:'column', gap:6, cursor:'pointer' }} onClick={() => setSelectedPatient(p)}>
              <div style={{ fontWeight:700, fontSize:14 }}>{p.name}</div>
              <div style={{ fontSize:11, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)' }}>{p.patientUID}</div>
              <div style={{ fontSize:12, color:'var(--color-ink-soft)', display:'flex', gap:8 }}>
                {p.age && <span>{p.age}y</span>}
                {p.gender && <span>{p.gender}</span>}
                {p.phone && <span>{p.phone}</span>}
              </div>
              {p.appointmentSlot && <div style={{ fontSize:11, color:'var(--color-primary)', fontWeight:600 }}>⏰ {p.appointmentSlot}</div>}
              <div style={{ display:'flex', gap:6, justifyContent:'space-between', alignItems:'center', marginTop:'auto', paddingTop:6 }}>
                <StatusBadge status={p.status} />
                <PaymentBadge status={p.billing?.paymentStatus||'unpaid'} />
              </div>
            </div>
          ))}
        </div>
      ) : listMode === 'compact' ? (
        /* ── Compact view ── */
        <div className="card" style={{ overflow:'hidden' }}>
          {filteredByMode.map((p,i) => (
            <div key={p._id} style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 14px', borderTop:i===0?'none':'1px solid var(--color-border)', background:i%2===0?'transparent':'var(--color-surface)' }}>
              <div style={{ flex:1, minWidth:0, cursor:'pointer' }} onClick={() => setSelectedPatient(p)}>
                <span style={{ fontWeight:700, fontSize:13 }}>{p.name}</span>
                <span style={{ marginLeft:10, fontSize:11, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)' }}>{p.patientUID}</span>
                {p.phone && <span style={{ marginLeft:10, fontSize:11, color:'var(--color-ink-faint)' }}>{p.phone}</span>}
                {p.appointmentSlot && <span style={{ marginLeft:10, fontSize:11, color:'var(--color-primary)', fontWeight:600 }}>{p.appointmentSlot}</span>}
              </div>
              <StatusBadge status={p.status} />
              <PaymentBadge status={p.billing?.paymentStatus||'unpaid'} />
              <button className="btn btn-icon btn-ghost" onClick={() => setSelectedPatient(p)}><Eye size={14}/></button>
            </div>
          ))}
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)', textAlign: 'left' }}>
                  <th style={thStyle}>Patient ID</th>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Age/Gender</th>
                  <th style={thStyle}>Phone</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Payment</th>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Slot</th>
                  <th style={thStyle}>Treatment Done</th>
                  <th style={thStyle}>Record</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredByMode.map((p) => (
                  <tr key={p._id} style={{ borderBottom: '1px solid var(--color-border)', opacity: p.isArchived ? 0.7 : 1 }}>
                    <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', color: 'var(--color-ink-faint)' }}>{p.patientUID}</td>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{p.name}</td>
                    <td style={tdStyle}>{p.age || '—'}{p.age ? ', ' : ''}{p.gender}</td>
                    <td style={tdStyle}>{p.phone || '—'}</td>
                    <td style={tdStyle}><StatusBadge status={p.status} /></td>
                    <td style={tdStyle}><PaymentBadge status={p.billing?.paymentStatus || 'unpaid'} /></td>
                    <td style={tdStyle}>{p.tokenDate}</td>
                    <td style={tdStyle}>
                      <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        {p.appointmentSlot ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--color-primary)', background: 'var(--color-primary-soft)', padding: '2px 8px', borderRadius: 6 }}>
                            🕐 {p.appointmentSlot}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--color-ink-faint)', fontSize: 12 }}>Walk-in</span>
                        )}
                        {p.isRescheduled && (
                          <span title={`Rescheduled — Original: ${p.rescheduledFrom||'—'} on ${p.rescheduledDate||'—'}${p.rescheduledTo ? ' → New: '+p.rescheduledTo : ''}`}
                            style={{ display:'inline-block', fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:100, background:'#fef3c7', color:'#d97706', border:'1px solid #fcd34d', cursor:'help', width:'fit-content' }}>
                            ⟳ Rescheduled
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      {(() => {
                        const diag = Array.isArray(p.diagnosis) ? p.diagnosis : [];
                        if (diag.length === 0) {
                          return <span style={{ color: 'var(--color-ink-faint)', fontSize: 12 }}>—</span>;
                        }
                        const treatTotal = diag.reduce((s, d) =>
                          s + (typeof d === 'object' ? (Number(d.finalPrice) || 0) : 0), 0);
                        return (
                          <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 6, overflow: 'hidden', minWidth: 180 }}>
                            {diag.map((d, i) => {
                              const name       = typeof d === 'string' ? d : (d.name || '');
                              const finalPrice = typeof d === 'object' ? (Number(d.finalPrice) || 0) : 0;
                              const discPct    = typeof d === 'object' ? (Number(d.discountPercent) || 0) : 0;
                              const catPrice   = typeof d === 'object' ? (Number(d.catalogPrice) || 0) : 0;
                              if (!name) return null;
                              return (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', borderBottom: i < diag.length - 1 ? '1px solid var(--color-border)' : 'none', gap: 8 }}>
                                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-ink)', flex: 1 }}>
                                    {name}
                                    {discPct > 0 && (
                                      <span style={{ fontSize: 10, marginLeft: 4, color: 'var(--color-warning)', fontWeight: 700 }}>{discPct}% off</span>
                                    )}
                                  </span>
                                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                    {discPct > 0 && catPrice > 0 && (
                                      <div style={{ fontSize: 10, color: 'var(--color-ink-faint)', textDecoration: 'line-through' }}>₹{catPrice.toLocaleString('en-IN')}</div>
                                    )}
                                    <div style={{ fontSize: 12, fontWeight: 700, color: finalPrice > 0 ? 'var(--color-success)' : 'var(--color-ink-faint)' }}>
                                      {finalPrice > 0 ? `₹${finalPrice.toLocaleString('en-IN')}` : '—'}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                            {treatTotal > 0 && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: 'var(--color-success-soft)', fontSize: 12, fontWeight: 800 }}>
                                <span style={{ color: 'var(--color-success)' }}>Total</span>
                                <span style={{ color: 'var(--color-success)' }}>₹{treatTotal.toLocaleString('en-IN')}</span>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={tdStyle}>
                      {p.isArchived ? (
                        <span className="badge" style={{ background: 'var(--color-border)', color: 'var(--color-ink-soft)' }}>
                          Archived
                        </span>
                      ) : (
                        <span className="badge badge-completed">Active</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap', minWidth: 100 }}>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'nowrap' }}>
                        {p.phone && (
                          <WhatsAppButton
                            patient={p}
                            slot={p.appointmentSlot}
                            date={p.tokenDate}
                            tokenNumber={p.tokenNumber}
                            size="xs"
                          />
                        )}
                        <button className="btn btn-icon btn-ghost" title="View / Edit" onClick={() => setSelectedPatient(p)}>
                          <Eye size={16} />
                        </button>
                        {!p.isArchived && (
                          <button className="btn btn-icon btn-ghost" title="Remove from active list" onClick={() => setDeleteTarget(p)}>
                            <Trash2 size={16} color="var(--color-danger)" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedPatient && (
        <PatientDetailModal
          patient={selectedPatient}
          editable={user?.role === 'doctor'}
          onClose={() => setSelectedPatient(null)}
          onUpdated={(updated) => {
            handleUpdated(updated);
            setSelectedPatient(updated);
          }}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title={`Remove ${deleteTarget?.name} from the active list?`}
        message="This patient will move to 'Archived / Past' and disappear from today's active queue. Their full record, history, and attachments stay safely stored and can always be found again using the Archived or All Records filter above."
        confirmLabel={deleting ? 'Removing...' : 'Remove from active list'}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </AppLayout>
  );
}

const thStyle = { padding: '12px 16px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-ink-faint)', fontWeight: 700 };
const tdStyle = { padding: '12px 16px', color: 'var(--color-ink)' };
